import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';
import {
	COLLECT_STATUS_MAP,
	ITrackingDB,
	TCollectStatus,
	TContentType,
} from 'type/tracking';

import TableCell from 'components/common/table/cell/TableCell';
import { useGetTrackingList } from 'hooks/queries/tracking';
import CorePrimaryButton from 'shared/button/CorePrimaryButton';
import CoreRadioChips from 'shared/chips/CoreRadioChips';
import CoreLoading from 'shared/CoreLoading';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import CoreTag from 'shared/tag/CoreTag';
import {
	Background,
	BodyInnerWrapper,
	BodyWrapper,
} from 'styles/styled-components/section';
import style from 'styles/styled-components/style';
import { ALL_GROUP, extractGroupList, filterByGroup } from 'utils/trackingGroup';

const CONTENT_TYPE_ICON: Record<string, JSX.Element> = {
	ig_feed: <Icon.Insta />,
	ig_video: <Icon.InstaReels />,
	yt_video: <Icon.Youtube />,
	yt_shorts: <Icon.YoutubeShorts />,
};

const CONTENT_TYPE_LABEL: Record<string, string> = {
	ig_feed: '피드',
	ig_video: '릴스',
	yt_video: '롱폼',
	yt_shorts: '쇼츠',
};

const formatDate = (dateStr: string) => {
	if (!dateStr) return '';
	const d = new Date(dateStr);
	if (isNaN(d.getTime())) return '';
	const yy = String(d.getFullYear()).slice(2);
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yy}.${mm}.${dd}`;
};

const getStatusColor = (status: TCollectStatus): 'green' | 'gray' => {
	return status === 'COLLECTING' ? 'green' : 'gray';
};

const COLUMN_WIDTHS = {
	title: 300,
	status: 100,
	contentCount: 100,
	contentTypes: 400,
	period: 220,
};

type FlexType = 'start' | 'center' | 'end' | 'between';

const HEADER_ITEMS: {
	header: string;
	name: string;
	width: number;
	align: FlexType;
}[] = [
	{
		header: '제목',
		name: 'title',
		width: COLUMN_WIDTHS.title,
		align: 'start',
	},
	{
		header: '수집 상태',
		name: 'status',
		width: COLUMN_WIDTHS.status,
		align: 'center',
	},
	{
		header: '콘텐츠 수',
		name: 'contentCount',
		width: COLUMN_WIDTHS.contentCount,
		align: 'end',
	},
	{
		header: '콘텐츠 유형',
		name: 'contentTypes',
		width: COLUMN_WIDTHS.contentTypes,
		align: 'start',
	},
	{
		header: '수집 기간',
		name: 'period',
		width: COLUMN_WIDTHS.period,
		align: 'start',
	},
];

const { Grid, Text } = style;

const TrackingList = () => {
	const navigate = useNavigate();
	const { data: trackingList, isLoading } = useGetTrackingList();

	const [selectedGroup, setSelectedGroup] = useState(ALL_GROUP);

	const groupList = useMemo(
		() => extractGroupList(trackingList ?? []),
		[trackingList],
	);

	const filteredList = useMemo(
		() => filterByGroup(trackingList ?? [], selectedGroup),
		[trackingList, selectedGroup],
	);

	const handleRowClick = (item: ITrackingDB) => {
		navigate(`/tracking/${item.code}`);
	};

	const filterContentTypes = (types: TContentType[]) =>
		types.filter((ct) => ct !== 'tt_content');

	return (
		<Background>
			<BodyWrapper
				options={{
					scrollbars: {
						autoHide: 'move',
						autoHideDelay: 250,
					},
				}}
			>
				<BodyInnerWrapper direction="column">
					<Grid direction="column" _margin="0 0 32px 0">
						<Text typo="h2" _margin="0 0 8px 0">
							콘텐츠 트래킹
						</Text>
						<Text typo="span" color="gray">
							등록된 트래킹 캠페인을 관리합니다.
						</Text>
					</Grid>

					<Grid _margin="0 0 16px 0">
						<CoreRadioChips
							group_type="multi_line"
							style_type="default"
							chips_list={groupList}
							selected_chips={selectedGroup}
							setSelectedChips={setSelectedGroup}
							_width="100%"
						/>
					</Grid>

					<div>
						<TableHeaderWrapper>
							{HEADER_ITEMS.map((item, idx) => (
								<>
									<Grid
										key={item.name}
										_width={`${item.width}px`}
										flex={item.align}
										align="center"
										_padding="0 10px"
									>
										<Text white_space="pre" typo="span">
											{item.header}
										</Text>
									</Grid>
									{idx !== HEADER_ITEMS.length - 1 && idx !== 0 && (
										<Contour _height="40%" _width="1px" />
									)}
								</>
							))}
						</TableHeaderWrapper>

						{isLoading ? (
							<Grid flex="center" _height="300px">
								<CoreLoading color_type="black" size_type="large" />
							</Grid>
						) : !trackingList || trackingList.length === 0 ? (
							<EmptyState>
								<Text typo="p" color="gray">
									등록된 트래킹 캠페인이 없습니다.
								</Text>
								<CorePrimaryButton
									size="default"
									style_type="tonal"
									text="피처링 솔루션 바로가기"
									_onClick={() =>
										window.open(
											'https://app.featuring.co',
											'_blank',
											'noopener,noreferrer',
										)
									}
								/>
							</EmptyState>
						) : filteredList.length === 0 ? (
							<EmptyState>
								<Text typo="p" color="gray">
									해당 그룹에 트래킹 캠페인이 없습니다.
								</Text>
							</EmptyState>
						) : (
							filteredList.map((item) => (
								<TableRow key={item.code} onClick={() => handleRowClick(item)}>
									<TableCell
										cell_type="common"
										_width={COLUMN_WIDTHS.title}
										_padding="0 10px"
									>
										{item.name}
									</TableCell>
									<TableCell cell_type="center" _width={COLUMN_WIDTHS.status}>
										<CoreTag
											tag_type="text"
											color_type={getStatusColor(item.collectStatus)}
											value={COLLECT_STATUS_MAP[item.collectStatus] ?? item.collectStatus}
										/>
									</TableCell>
									<TableCell cell_type="number" _width={COLUMN_WIDTHS.contentCount}>
										{`${(item.contentCount ?? 0).toLocaleString()}개`}
									</TableCell>
									<TableCell
										cell_type="common"
										_width={COLUMN_WIDTHS.contentTypes}
										_padding="0 10px"
									>
										<ContentTypeBadges>
											{filterContentTypes(item.contentTypes).map((ct) => (
												<ContentTypeBadge key={ct}>
													{CONTENT_TYPE_ICON[ct] && (
														<CoreSvg _size="14px">{CONTENT_TYPE_ICON[ct]}</CoreSvg>
													)}
													{CONTENT_TYPE_LABEL[ct] ?? ct}
												</ContentTypeBadge>
											))}
										</ContentTypeBadges>
									</TableCell>
									<TableCell
										cell_type="common"
										_width={COLUMN_WIDTHS.period}
										_padding="0 10px"
									>
										{`${formatDate(item.trackingStartDate)} ~ ${formatDate(
											item.trackingEndDate,
										)}`}
									</TableCell>
								</TableRow>
							))
						)}
					</div>
				</BodyInnerWrapper>
			</BodyWrapper>
		</Background>
	);
};

const TableHeaderWrapper = styled(Grid)`
	height: 66px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	padding: 0 16px;

	${({ theme }) => theme.common.flexStart};
`;

const TableRow = styled(Grid)`
	min-width: calc(100% - 32px);
	height: 65px;

	border-radius: 8px;
	margin-top: 8px;
	padding: 0 16px;
	cursor: pointer;

	${({ theme }) => theme.common.flexStart}

	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray_white};

	&:hover {
		border-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
	}
`;

const Contour = styled(Grid)`
	background-color: ${({ theme }) => theme.colors.gray.gray15};
`;

const ContentTypeBadges = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
`;

const ContentTypeBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	${({ theme }) => theme.typo.body.span};
	background: ${({ theme }) => theme.colors.gray.gray05_bg};
	border-radius: 4px;
	padding: 2px 6px;
	white-space: nowrap;
`;

const EmptyState = styled.div`
	width: 100%;
	height: 300px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
`;

export default TrackingList;
